import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useEffect, useCallback } from "react";

// Types
export type ChatSessionStatus = "waiting" | "active" | "closed";

export interface ChatSession {
  id: string;
  user_id: string;
  assigned_to: string | null;
  status: ChatSessionStatus;
  started_at: string;
  ended_at: string | null;
  converted_ticket_id: string | null;
  // Joined fields
  user_email?: string;
  user_name?: string;
  assigned_to_name?: string;
  unread_count?: number;
  last_message?: string;
  last_message_at?: string;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  sender_id: string;
  sender_type: "user" | "admin";
  content: string;
  created_at: string;
  // Joined fields
  sender_name?: string;
}

// =============================================
// QUERIES
// =============================================

// Get user's active chat session
export function useMyActiveSession() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["my-active-chat-session", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("support_chat_sessions")
        .select("*")
        .eq("user_id", user.id)
        .neq("status", "closed")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ChatSession | null;
    },
    enabled: !!user,
  });
}

// Get all chat sessions (admin)
export function useAllChatSessions(statusFilter?: ChatSessionStatus) {
  return useQuery({
    queryKey: ["all-chat-sessions", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("support_chat_sessions")
        .select("*")
        .order("started_at", { ascending: false });

      if (statusFilter) {
        query = query.eq("status", statusFilter);
      } else {
        query = query.neq("status", "closed");
      }

      const { data: sessions, error } = await query;
      if (error) throw error;

      if (!sessions || sessions.length === 0) return [];

      // Get user info
      const userIds = [...new Set(sessions.map((s) => s.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, email, first_name, last_name")
        .in("user_id", userIds);

      const profileMap = new Map(
        profiles?.map((p) => [
          p.user_id,
          { email: p.email, name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email },
        ]) || []
      );

      // Get last message for each session
      const sessionIds = sessions.map((s) => s.id);
      const { data: lastMessages } = await supabase
        .from("support_chat_messages")
        .select("session_id, content, created_at")
        .in("session_id", sessionIds)
        .order("created_at", { ascending: false });

      const lastMessageMap = new Map<string, { content: string; created_at: string }>();
      lastMessages?.forEach((m) => {
        if (!lastMessageMap.has(m.session_id)) {
          lastMessageMap.set(m.session_id, { content: m.content, created_at: m.created_at });
        }
      });

      return sessions.map((session) => {
        const profile = profileMap.get(session.user_id);
        const lastMsg = lastMessageMap.get(session.id);
        return {
          ...session,
          user_email: profile?.email || "N/A",
          user_name: profile?.name || "N/A",
          last_message: lastMsg?.content,
          last_message_at: lastMsg?.created_at,
        } as ChatSession;
      });
    },
  });
}

// Get chat messages
export function useChatMessages(sessionId: string | undefined) {
  return useQuery({
    queryKey: ["chat-messages", sessionId],
    queryFn: async () => {
      if (!sessionId) return [];

      const { data: messages, error } = await supabase
        .from("support_chat_messages")
        .select("*")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (!messages || messages.length === 0) return [];

      // Get sender names
      const senderIds = [...new Set(messages.map((m) => m.sender_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name")
        .in("user_id", senderIds);

      const profileMap = new Map(
        profiles?.map((p) => [
          p.user_id,
          `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Utilisateur",
        ]) || []
      );

      return messages.map((msg) => ({
        ...msg,
        sender_name: profileMap.get(msg.sender_id) || "Utilisateur",
      })) as ChatMessage[];
    },
    enabled: !!sessionId,
  });
}

// Get waiting sessions count
export function useWaitingSessionsCount() {
  return useQuery({
    queryKey: ["waiting-chat-sessions-count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_waiting_chat_sessions_count");
      if (error) throw error;
      return data as number;
    },
    refetchInterval: 15000, // More frequent for live chat
  });
}

// =============================================
// REAL-TIME HOOKS
// =============================================

export function useChatMessagesSubscription(
  sessionId: string | undefined,
  onNewMessage?: (message: ChatMessage) => void
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`chat-messages-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_chat_messages",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;
          queryClient.invalidateQueries({ queryKey: ["chat-messages", sessionId] });
          onNewMessage?.(newMessage);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, queryClient, onNewMessage]);
}

export function useChatSessionsSubscription(onUpdate?: () => void) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("chat-sessions-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "support_chat_sessions",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
          queryClient.invalidateQueries({ queryKey: ["waiting-chat-sessions-count"] });
          onUpdate?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, onUpdate]);
}

// =============================================
// MUTATIONS
// =============================================

// Start chat session (user)
export function useStartChatSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (initialMessage?: string) => {
      if (!user) throw new Error("Non authentifié");

      // Check for existing active session
      const { data: existing } = await supabase
        .from("support_chat_sessions")
        .select("id")
        .eq("user_id", user.id)
        .neq("status", "closed")
        .limit(1)
        .maybeSingle();

      if (existing) {
        return existing;
      }

      // Create new session
      const { data: session, error } = await supabase
        .from("support_chat_sessions")
        .insert({ user_id: user.id })
        .select()
        .single();

      if (error) throw error;

      // Send initial message if provided
      if (initialMessage) {
        await supabase.from("support_chat_messages").insert({
          session_id: session.id,
          sender_id: user.id,
          sender_type: "user",
          content: initialMessage,
        });
      }

      return session;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-active-chat-session"] });
      queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["waiting-chat-sessions-count"] });
    },
    onError: (error) => {
      console.error("Start chat error:", error);
      toast.error("Erreur lors du démarrage du chat");
    },
  });
}

// Send chat message
export function useSendChatMessage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      sessionId,
      content,
      senderType,
    }: {
      sessionId: string;
      content: string;
      senderType?: "user" | "admin";
    }) => {
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("support_chat_messages")
        .insert({
          session_id: sessionId,
          sender_id: user.id,
          sender_type: senderType || "user",
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["chat-messages", variables.sessionId] });
      queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
    },
    onError: (error) => {
      console.error("Send chat message error:", error);
      toast.error("Erreur lors de l'envoi du message");
    },
  });
}

// Accept chat session (admin)
export function useAcceptChatSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!user) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("support_chat_sessions")
        .update({
          status: "active",
          assigned_to: user.id,
        })
        .eq("id", sessionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["waiting-chat-sessions-count"] });
      toast.success("Session acceptée");
    },
    onError: (error) => {
      console.error("Accept chat error:", error);
      toast.error("Erreur lors de l'acceptation");
    },
  });
}

// Close chat session
export function useCloseChatSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("support_chat_sessions")
        .update({
          status: "closed",
          ended_at: new Date().toISOString(),
        })
        .eq("id", sessionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["my-active-chat-session"] });
      queryClient.invalidateQueries({ queryKey: ["waiting-chat-sessions-count"] });
      toast.success("Session fermée");
    },
    onError: (error) => {
      console.error("Close chat error:", error);
      toast.error("Erreur lors de la fermeture");
    },
  });
}

// Convert chat to ticket
export function useConvertChatToTicket() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      sessionId,
      subject,
      category,
    }: {
      sessionId: string;
      subject: string;
      category: "help" | "billing" | "technical" | "refund" | "other";
    }) => {
      if (!user) throw new Error("Non authentifié");

      // Get session to find user_id
      const { data: session, error: sessionError } = await supabase
        .from("support_chat_sessions")
        .select("user_id")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Get chat messages for description
      const { data: messages } = await supabase
        .from("support_chat_messages")
        .select("sender_type, content, created_at")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true })
        .limit(10);

      const description = messages
        ?.map((m) => `[${m.sender_type}]: ${m.content}`)
        .join("\n\n") || "";

      // Create ticket
      const { data: ticket, error: ticketError } = await supabase
        .from("support_tickets")
        .insert([{
          user_id: session.user_id,
          subject,
          description: `Converti depuis le chat:\n\n${description}`,
          category,
          priority: "medium",
        }] as never)
        .select()
        .single();

      if (ticketError) throw ticketError;

      // Update session
      await supabase
        .from("support_chat_sessions")
        .update({
          converted_ticket_id: ticket.id,
          status: "closed",
          ended_at: new Date().toISOString(),
        })
        .eq("id", sessionId);

      return ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-chat-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["all-support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["pending-tickets-count"] });
      toast.success("Chat converti en ticket");
    },
    onError: (error) => {
      console.error("Convert chat error:", error);
      toast.error("Erreur lors de la conversion");
    },
  });
}
